import { createFreePreviewMember, fetchMemberFreePreviewEligibility } from 'actions/LuxPlusActions'
import clsx from 'clsx'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
import Faq from 'components/Common/Faq'
import FormatCurrency from 'components/Common/FormatCurrency'
import HeadData from 'components/Common/HeadData/HeadData'
import LayoutContainer from 'components/Common/LayoutContainer'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import Pane from 'components/Common/Pane'
import PaneBody from 'components/Common/Pane/PaneBody'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import VideoLightbox from 'LuxKit/Carousel/VideoLightbox'
import LinePlayIcon from 'LuxKit/Icons/line/LinePlayIcon'
import ModalContext from 'contexts/ModalContext'
import Pill from 'LuxKit/Pill'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { INTL_DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import HeroImage from 'content/components/HeroImage'
import { LuxPlusLabelProvider } from 'contexts/LuxPlusLabelContext'
import { useAppDispatch } from 'hooks/reduxHooks'
import { dateDifference, isSameDay, useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { getThemeClassName } from 'lib/theme/themeUtils'
import LuxPlusFreeCtaSection from 'luxPlus/components/LandingPage/LuxPlusFreePreviewCtaSection'
import LuxPlusLandingPageBenefit from 'luxPlus/components/LandingPage/LuxPlusLandingPageBenefit'
import LuxPlusSubscriptionTsAndCs from 'luxPlus/components/LuxPlusSubscriptionTsAndCs'
import LuxPlusLogoWrapper from 'luxPlus/components/LuxPlusLogoWrapper'
import LuxPlusOfferCarousels from 'luxPlus/components/LuxPlusOfferCarousels'
import { LUXURY_PLUS, LUXURY_PLUS_PATHS } from 'luxPlus/constants/base'
import useLuxPlusFreePreviewUtils from 'luxPlus/hooks/useLuxPlusFreePreviewUtils'
import {
  getCanRedeemLuxPlusBenefits,
  getIsEnabledLuxPlusDiscountedBookingProtection,
  getIsEnabledLuxPlusDiscountedInsurance,
  getIsExpiredLuxPlusMemberInGracePeriod,
  getIsFreePreviewLuxPlusMember,
  getIsPendingRenewalLuxPlusMember,
} from 'luxPlus/selectors/featureToggle'
import { rem } from 'polished'
import { useCallback, useContext, useEffect, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { selectLoggedIn } from 'selectors/accountSelectors'
import styled from 'styled-components'
import LuxPlusRenewalMessageBanner from 'luxPlus/components/Banners/LuxPlusRenewalMessageBanner'
import useEnabledLuxPlusBenefits from 'luxPlus/hooks/useEnabledLuxPlusBenefits'
import { arrayToObject } from 'lib/array/arrayUtils'
import GeoContext from 'contexts/geoContext'
import { SAVE_THOUSANDS_REGIONS } from 'luxPlus/constants/benefits'
import Sheet from 'components/utils/Sheet'
import purchaseLuxPlus from 'actions/luxPlus/purchaseLuxPlus'

const EXCLUDED_NON_MEMBER_BENEFITS: Array<App.LuxPlusBenefitType> = ['partner_promo_code', 'voucher']

const StyledHero = styled(HeroImage)`
  padding-inline: 0;
  min-height: ${rem(342)};

  ${mediaQueryUp.tablet} {
    min-height: ${rem(400)};
  }
`

const InnerContainer = styled.div`
  position: relative;

  &.free-preview-member {
    max-width: ${rem(804)};
  }
`

const FreePreviewContainer = styled.div`
  max-width: ${rem(400)};
`

const BenefitsListContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${rem(24)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(3, 1fr);
  }

  ${mediaQueryUp.desktop} {
    grid-template-columns: repeat(4, 1fr);
    gap: ${rem(32)} ${rem(20)};
  }
`

interface MappedProps {
  subscriptionRenewalDate?: string
  isPendingRenewalLuxPlusMember: boolean
  canRedeemLuxPlusBenefits: boolean
  isFreePreviewLuxPlusMember: boolean
  freePreviewExpiryDate: string
  firstName: string
  subscriptionProduct: App.ProductSubscription
  isLoggedIn: boolean
  isSpoofed: boolean
  isEnabledLuxPlusDiscountedInsurance: boolean
  isEnabledLuxPlusDiscountedBookingProtection: boolean
  isExpiredLuxPlusMemberInGracePeriod: boolean
}

interface Props extends MappedProps {}

interface VideoConfigData {
  id: string
}

const restOfTheWorldSaveHundredsRegions = arrayToObject(['SG', 'DE', 'CA', 'MY', 'HK', 'NL', 'AE', 'IE', 'FR', 'ES', 'IT', 'SA', 'CN', 'QA', 'MO', 'IL'],
  regionCode => regionCode,
  () => ({
    id: '1047672568',
  }),
)

const restOfTheWorldSaveThousandsRegions = arrayToObject(SAVE_THOUSANDS_REGIONS,
  regionCode => regionCode,
  () => ({
    id: '1048439047',
  }),
)

const regionVideoConfig: Record<string, VideoConfigData> = {
  AU: {
    id: '1047672501',
  },
  NZ: {
    id: '1047672501',
  },
  GB: {
    id: '1047672633',
  },
  US: {
    id: '1047672671',
  },
  ...restOfTheWorldSaveHundredsRegions,
  ...restOfTheWorldSaveThousandsRegions,
}

function LuxPlusLandingPageContent({
  canRedeemLuxPlusBenefits,
  isFreePreviewLuxPlusMember,
  freePreviewExpiryDate,
  firstName,
  subscriptionProduct,
  isLoggedIn,
  // when we're spoofing (admin/cs is in control), we allow joining up without a join fee
  isSpoofed,
  isEnabledLuxPlusDiscountedInsurance,
  isEnabledLuxPlusDiscountedBookingProtection,
  isPendingRenewalLuxPlusMember,
  isExpiredLuxPlusMemberInGracePeriod,
}: Props) {
  const { currentRegionCode } = useContext(GeoContext)
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const { showFreePreviewSignup, shouldAutoEnrolFreePreview } = useLuxPlusFreePreviewUtils()
  const showModal = useContext(ModalContext)

  const openLogin = useCallback(() => {
    return showModal<AccountAccessModalResult>(<AccountAccessModal />)
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const purchaseLuxPlusWithNoJoinFee = useCallback(() => {
    dispatch(purchaseLuxPlus({ hasJoinFee: false }))
  }, [dispatch])

  const purchaseLuxPlusWithJoinFee = useCallback(() => {
    dispatch(purchaseLuxPlus({ hasJoinFee: true }))
  }, [dispatch])

  useEffect(() => {
    if (isLoggedIn) {
      dispatch(fetchMemberFreePreviewEligibility())
    }
  }, [dispatch, isLoggedIn])

  useEffect(() => {
    // If not logged in and autoEnrollFreePreview is true, open login modal
    if (!isLoggedIn && shouldAutoEnrolFreePreview) {
      openLogin()
    }
    if (isLoggedIn && shouldAutoEnrolFreePreview) {
      dispatch(createFreePreviewMember())
    }
  }, [shouldAutoEnrolFreePreview, dispatch, isLoggedIn, openLogin])

  const videoConfig: VideoConfigData | undefined = regionVideoConfig[currentRegionCode]

  const openLuxPlusPromotionalVideo = useCallback(() => {
    showModal(<VideoLightbox videoId={videoConfig.id} playOnMount />)
  }, [showModal, videoConfig?.id])

  const [offer, joinFeeFreeOffer] = useMemo(() => [
    subscriptionProduct.offers?.find(offer => !offer.joinFeeWaived),
    subscriptionProduct.offers?.find(offer => offer.joinFeeWaived),
  ], [subscriptionProduct.offers])

  const enabledBenefits = useEnabledLuxPlusBenefits(offer?.benefits).filter(benefit => !EXCLUDED_NON_MEMBER_BENEFITS.includes(benefit.type))

  const isLuxPlusMemberCanManuallyRenew = isPendingRenewalLuxPlusMember || isExpiredLuxPlusMemberInGracePeriod

  const intlDateFormatter = useIntlDateFormatter()

  const heading = useMemo(() => {
    if (canRedeemLuxPlusBenefits) {
      if (firstName) {
        return intl.formatMessage(
          {
            id: '7mXp-h-Zks8-dwFTWqXM',
            defaultMessage: '{firstName}, welcome to the VIP travel club!',
            description: 'Personalized heading greeting for LuxPlus members on landing page',
          },
          { firstName },
        )
      }
      return intl.formatMessage({
        id: 'CmpH6i7Xak8dbAysK6B4',
        defaultMessage: 'Welcome to the VIP travel club!',
        description: 'Heading greeting for LuxPlus members without name on landing page',
      })
    }

    if (isFreePreviewLuxPlusMember) {
      if (firstName) {
        return intl.formatMessage(
          {
            id: 'ZQkOPdr4FYq7QJDwpsxo',
            defaultMessage: 'Welcome to your free preview of our VIP Travel Club, {firstName}!',
            description: 'Personalized heading for free preview members on landing page',
          },
          { firstName },
        )
      }
      return intl.formatMessage({
        id: 'OrpzIy7sMQZQFEXCm6KS',
        defaultMessage: 'Welcome to your free preview of our VIP Travel Club!',
        description: 'Section heading for free preview members on the landing page',
      })
    }

    return intl.formatMessage({
      id: 'znMueYPDQ4_v8XOFLr6W',
      defaultMessage: 'Your VIP travel club',
      description: 'Default heading on LuxPlus landing page for non-members',
    })
  }, [firstName, canRedeemLuxPlusBenefits, isFreePreviewLuxPlusMember, intl])

  const activeFreePreviewPillCopy = useMemo(() => {
    const dateDifferenceFreePreview = dateDifference(new Date(freePreviewExpiryDate))
    const expiresToday = isSameDay(new Date(), new Date(freePreviewExpiryDate))
    const days = expiresToday ? 0 : (dateDifferenceFreePreview.days <= 1 ? 1 : dateDifferenceFreePreview.days)

    return intl.formatMessage(
      {
        id: 'ERHTeQ0VNNMIYOiSxqZZ',
        defaultMessage: '{days, plural, =0 {Preview ends today} one {Preview ends in # day} other {Preview ends in # days}}',
        description: 'Pill label showing how many days until free preview period expires',
      },
      { days },
    )
  }, [freePreviewExpiryDate, intl])

  const subheading = isFreePreviewLuxPlusMember ?
      intl.formatMessage(
        {
          id: '7Zr2eoICwSlVtCPmu03W',
          defaultMessage: 'Your free preview is available until {date} or when you make your first {programName} purchase.',
          description: 'Subheading explaining free preview availability and expiry on landing page',
        },
        { date: intlDateFormatter(freePreviewExpiryDate, INTL_DMY_CASUAL_SHORT_FORMAT), programName: LUXURY_PLUS.PROGRAM_NAME },
      ) :
      intl.formatMessage({
        id: 'DgrIBn1qkb3a4m8U7Ryo',
        defaultMessage: 'Unpack a world of member-only benefits',
        description: 'Subheading on LuxPlus landing page for non-free-preview users',
      })

  return <LuxPlusLabelProvider page="landing-page">
    <HeadData
      title={intl.formatMessage(
        {
          id: 'axJ5FkODh98xXjLFV3Ho',
          defaultMessage: '{programName} By Luxury Escapes - Your VIP Travel Club',
          description: 'Page title for LuxPlus landing page SEO on the promotional landing page',
        },
        { programName: LUXURY_PLUS.PROGRAM_NAME },
      )}
      description={intl.formatMessage(
        {
          id: 'X1BA6MEiBgGuc8Ze0tnz',
          defaultMessage: 'Save on exclusive luxury travel deals with {programName} travel club with special offers at insider prices.',
          description: 'HTML meta description for LuxPlus landing page SEO',
        },
        { programName: LUXURY_PLUS.PROGRAM_NAME },
      )}
    />
    <StyledHero
      className={getThemeClassName({ mobile: 'inverse' })}
      id="atq9zn34ynod9b0d6wb"
      brightness={-25}
      mobileAspectRatio="7:6"
      tabletAspectRatio="9:5"
      desktopAspectRatio="18:5"
      largeDesktopAspectRatio="10:3"
    >
      <LayoutContainer>
        <InnerContainer className={clsx({ 'free-preview-member': isFreePreviewLuxPlusMember })}>
          <VerticalSpacer gap={16}>
            <Group direction="vertical" tabletDirection="horizontal" tabletHorizontalAlign="space-between" tabletVerticalAlign="center" gap={16}>
              <VerticalSpacer gap={12}>
                <LuxPlusLogoWrapper height={20} />
                <Group direction="vertical" gap={12} tabletGap={4} horizontalAlign="start">
                  <Heading variant="heading1" colour="neutral-one" format="propercase">{heading}</Heading>
                  {isFreePreviewLuxPlusMember &&
                    <Pill
                      kind="primary"
                      variant="success"
                    >
                      {activeFreePreviewPillCopy}
                    </Pill>}
                  <Heading variant="heading5" colour="neutral-one">{subheading}</Heading>
                </Group>
                {canRedeemLuxPlusBenefits &&
                  <TextButton
                    kind="primary"
                    size="medium"
                    to={`/${LUXURY_PLUS_PATHS.ACCOUNT_MANAGEMENT_PAGE}`}
                    fit="mobile-full-width"
                  >
                    {intl.formatMessage({
                      id: 'CDqJcSc6WiU9-E2HDLiS',
                      defaultMessage: 'Manage membership',
                      description: 'Button to navigate to LuxPlus membership management page',
                    })}
                  </TextButton>}
                {!canRedeemLuxPlusBenefits && <>
                  <Group direction="vertical" tabletDirection="horizontal" gap={16}>
                    {videoConfig && <TextButton
                      kind="secondary"
                      startIcon={<LinePlayIcon />}
                      fit="mobile-full-width"
                      onClick={openLuxPlusPromotionalVideo}
                    >
                      {intl.formatMessage({
                        id: 'wi5GuWr1Iojp00JLCoeA',
                        defaultMessage: 'Watch video',
                        description: 'Button to play the LuxPlus promotional video on landing page',
                      })}
                    </TextButton>}
                    {!isLuxPlusMemberCanManuallyRenew && <TextButton
                      kind="primary"
                      disabled={!subscriptionProduct.offers}
                      onClick={purchaseLuxPlusWithJoinFee}
                      fit="mobile-full-width"
                    >
                      {intl.formatMessage(
                        {
                          id: 'vL4K_M2yS67rkr2bCEXW',
                          defaultMessage: 'Become a {programName} member',
                          description: 'Button to start LuxPlus membership purchase for the membership',
                        },
                        { programName: LUXURY_PLUS.PROGRAM_NAME },
                      )}
                    </TextButton>}
                    {isLuxPlusMemberCanManuallyRenew && <TextButton
                      kind="primary"
                      disabled={!subscriptionProduct.offers}
                      onClick={purchaseLuxPlusWithNoJoinFee}
                      fit="mobile-full-width"
                    >
                      {intl.formatMessage(
                        {
                          id: 'ypp0Ak5NC9oHqB4wHdYV',
                          defaultMessage: 'Renew your {programName} membership',
                          description: 'Button to renew an expired or pending LuxPlus membership',
                        },
                        { programName: LUXURY_PLUS.PROGRAM_NAME },
                      )}
                    </TextButton>}
                    {isSpoofed && joinFeeFreeOffer &&
                      <TextButton
                        kind="primary"
                        disabled={!subscriptionProduct.offers}
                        onClick={purchaseLuxPlusWithNoJoinFee}
                        fit="mobile-full-width"
                      >
                        {intl.formatMessage(
                          {
                            id: 'AuISBRBkhI5bJc9_PRL4',
                            defaultMessage: 'Become a {programName} member (no joining fee)',
                            description: 'Button for admin/CS to purchase LuxPlus membership without joining fee',
                          },
                          { programName: LUXURY_PLUS.PROGRAM_NAME },
                        )}
                      </TextButton>}
                  </Group>
                  {!isLoggedIn && <BodyText variant="medium" colour="neutral-one">
                    <FormattedMessage
                      id="YkXFz1GUcQmdL6ue-oAk"
                      defaultMessage="Already a {programName} member? <link>Log in</link>"
                      description="Prompt for existing LuxPlus members to log in on landing page"
                      values={{
                        programName: LUXURY_PLUS.PROGRAM_NAME,
                        link: (chunks) => <TextLink size="medium" onClick={openLogin}>{chunks}</TextLink>,
                      }}
                    />
                  </BodyText>}
                </>}
              </VerticalSpacer>
              {showFreePreviewSignup && !isLuxPlusMemberCanManuallyRenew && !shouldAutoEnrolFreePreview &&
                <CSSBreakpoint min="tablet">
                  <FreePreviewContainer>
                    <LuxPlusFreeCtaSection />
                  </FreePreviewContainer>
                </CSSBreakpoint>}
            </Group>
            <LuxPlusRenewalMessageBanner />
          </VerticalSpacer>
        </InnerContainer>
      </LayoutContainer>
    </StyledHero>
    {showFreePreviewSignup && !isLuxPlusMemberCanManuallyRenew && !shouldAutoEnrolFreePreview &&
      <CSSBreakpoint max="mobile">
        <LuxPlusFreeCtaSection />
      </CSSBreakpoint>}

    <Sheet background="lux-plus" padding="40 60" paletteType="inverse">
      <LayoutContainer>
        {subscriptionProduct.fetching && <LoadingIndicator inline>{intl.formatMessage({
          id: '0pQp7QyWxjaSgxg_LL8d',
          defaultMessage: 'Loading...',
          description: 'Loading indicator while LuxPlus subscription data is being fetched',
        })}
        </LoadingIndicator>}
        {offer && <VerticalSpacer gap={40}>
          <VerticalSpacer gap={8}>
            <Heading variant="heading3" colour="neutral-one">{intl.formatMessage({
              id: 'kYbaFZCzIJCxVRJFiWWa',
              defaultMessage: 'Unlimited benefits all year round',
              description: 'Heading for the benefits section on LuxPlus landing page',
            })}
            </Heading>
            {!canRedeemLuxPlusBenefits && <>
              <BodyText variant="medium" colour="neutral-one">
                <FormattedMessage
                  id="DhsMAjvCuOoUsRA30Szj"
                  defaultMessage="As a member of the {programName} VIP travel club you’ll enjoy unlimited benefits all year round for {yearlyPrice}/year, and a one-off joining fee of {joinFee}."
                  description="Description of LuxPlus+ membership pricing including yearly cost and joining fee on the landing page"
                  values={{
                    programName: LUXURY_PLUS.PROGRAM_NAME,
                    yearlyPrice: <FormatCurrency value={offer.price} />,
                    joinFee: <FormatCurrency value={offer.joinFee} />,
                  }}
                />
              </BodyText>
              <BodyText variant="medium" weight="bold" colour="highlight-primary">
                <FormattedMessage
                  id="5P7pELyh5LG9quyGqlgh"
                  defaultMessage="Special Offer: No joining fee when you book a {programName} offer (save {joinFee})."
                  description="Promotional message highlighting no joining fee when booking a LuxPlus+ offer on the landing page"
                  values={{
                    programName: LUXURY_PLUS.PROGRAM_NAME,
                    joinFee: <FormatCurrency value={offer.joinFee} />,
                  }}
                />
              </BodyText>
            </>}
          </VerticalSpacer>
          <BenefitsListContainer>
            {enabledBenefits.map((benefit) => (
              <LuxPlusLandingPageBenefit key={benefit.title} benefit={benefit} />
            ))}
          </BenefitsListContainer>
          <LuxPlusSubscriptionTsAndCs />
        </VerticalSpacer>}
      </LayoutContainer>
    </Sheet>
    <Pane>
      <PaneBody size="xl-y">
        <VerticalSpacer gap={40}>
          <LuxPlusOfferCarousels analyticsPage="LUXPLUS" />
          <LayoutContainer>
            <Heading variant="heading3" align="center" as="h2">{intl.formatMessage({
              id: 'NGIoltNIETMlzD5KQHX7',
              defaultMessage: 'Frequently Asked Questions',
              description: 'Heading for the FAQ section on LuxPlus landing page',
            })}
            </Heading>
            <Faq slug="luxplus-faqs" />
          </LayoutContainer>
        </VerticalSpacer>
      </PaneBody>
    </Pane>
    {(isEnabledLuxPlusDiscountedInsurance || isEnabledLuxPlusDiscountedBookingProtection) && <Pane>
      <PaneBody size="xl-y">
        <LayoutContainer>
          <LuxPlusSubscriptionTsAndCs type="insuranceAndBookingProtection" />
        </LayoutContainer>
      </PaneBody>
    </Pane>}
  </LuxPlusLabelProvider>
}

const mapStateToProps = (state: App.State): MappedProps => ({
  canRedeemLuxPlusBenefits: getCanRedeemLuxPlusBenefits(state),
  isFreePreviewLuxPlusMember: getIsFreePreviewLuxPlusMember(state),
  freePreviewExpiryDate: state.auth.account.luxPlus.member.subscription.item?.expiryDate || '',
  firstName: state.auth.account.givenName,
  subscriptionProduct: state.luxPlus.products.subscription,
  isLoggedIn: selectLoggedIn(state),
  isSpoofed: state.auth.account.isSpoofed,
  isPendingRenewalLuxPlusMember: getIsPendingRenewalLuxPlusMember(state),
  isEnabledLuxPlusDiscountedInsurance: getIsEnabledLuxPlusDiscountedInsurance(state),
  isEnabledLuxPlusDiscountedBookingProtection: getIsEnabledLuxPlusDiscountedBookingProtection(state),
  isExpiredLuxPlusMemberInGracePeriod: getIsExpiredLuxPlusMemberInGracePeriod(state),
})

export default connect(mapStateToProps)(LuxPlusLandingPageContent)
